import { APP_HEADER, type ApiErrorBody, type ErrorCode } from '@blink/shared';

/** Error from the API, carrying the stable code the app translates. */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | 'NETWORK',
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

const AUTH_PAGES = ['/login'];

/**
 * Calls the API through this app's /api proxy (same origin, cookies included).
 * Every call names the courier app, so the server uses the courier-app session (D-0008).
 * A 401 outside the sign-in page sends the courier back to sign in.
 */
export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  let res: Response;
  const form = init.body instanceof FormData;
  try {
    res = await fetch(`/api${path}`, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      // a photo goes as a form (the browser sets its own content type)
      headers: { [APP_HEADER]: 'courier', ...(init.body !== undefined && !form ? { 'Content-Type': 'application/json' } : {}) },
      body: form ? (init.body as FormData) : init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ApiClientError(0, 'NETWORK', 'Network error');
  }

  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as (ApiErrorBody & T) | null;
  if (!res.ok) {
    const error = body?.error ?? { code: 'INTERNAL' as const, message: res.statusText };
    if (res.status === 401 && !AUTH_PAGES.includes(window.location.pathname)) {
      window.location.href = `/login?reason=${error.code === 'SESSION_EXPIRED' ? 'expired' : 'signin'}`;
    }
    throw new ApiClientError(res.status, error.code, error.message, error.details);
  }
  return body as T;
}

// ---- response types

export interface CourierMe {
  courier: { id: string; name: string; employeeCode: string; vehicleType: string; vehiclePlate: string | null };
  hub: { code: string; name: string } | null;
  today: string;
}

export interface AuthMe {
  user: { id: string; fullName: string; mustChangePassword: boolean; language: string };
  sessionIdleMinutes: number;
}

export type CourierStatus = 'ASSIGNED' | 'OUT_FOR_DELIVERY' | 'IN_TRANSIT' | 'ON_HOLD';

export interface DayParcel {
  id: string;
  awb: string;
  status: CourierStatus;
  /** masked: first name + initial */
  consignee: string;
  district: string | null;
  pieceCount: number;
  codFils: number;
  timeSlot: string | null;
  /** given for an earlier day and still open */
  carriedOver: boolean;
  /** every piece already in the vehicle: waiting only for Start route */
  loaded: boolean;
  /** place in today's route order (1 = first), null when not in the order */
  stop: number | null;
  /** planned arrival time */
  etaAt: string | null;
}

export interface CourierDay {
  date: string;
  summary: { parcels: number; pieces: number; codFils: number; toLoad: number; loaded: number; onRoute: number; onHold: number; delivered: number; failed: number };
  route: { km: number; finishAt: string; plannedAt: string; mode: 'MOCK' | 'OSRM' | 'MOCK_FALLBACK'; from: 'HUB' | 'GPS'; outdated: boolean } | null;
  next: DayParcel | null;
  districts: { name: string | null; parcels: DayParcel[] }[];
}

export interface GoAnswer {
  shipmentIds: string[];
  pin: { latitude: number; longitude: number } | null;
  /** true: no customer pin, the district centre is used */
  approximate: boolean;
}

export interface ParcelDetails {
  id: string;
  awb: string;
  status: CourierStatus;
  merchant: string;
  consigneeName: string;
  phone: string;
  altPhone: string | null;
  addressText: string | null;
  city: string | null;
  district: string | null;
  pin: { latitude: number; longitude: number } | null;
  codFils: number;
  pieceCount: number;
  pieces: { label: string; barcode: string; status: string; state: PieceState }[];
  weightGrams: number | null;
  deliveryType: string | null;
  timeSlot: string | null;
  description: string | null;
  attemptCount: number;
  taskDate: string;
  calls: ParcelCalls;
}

/** Where a piece is now, for the courier. */
export type PieceState = 'EXPECTED' | 'IN_HUB' | 'IN_VEHICLE' | 'AT_DOOR';

export type PodKind = 'POD_EMIRATES_ID' | 'POD_VILLA' | 'POD_DOOR';
export type PaymentMethod = 'CASH' | 'PAYMENT_LINK';

export interface DeliveryView {
  id: string;
  awb: string;
  codFils: number;
  pieces: { label: string; barcode: string; scanned: boolean }[];
  photo: { id: string; kind: PodKind; takenAt: string } | null;
  paymentMethods: PaymentMethod[];
  manual: ManualAwbStatus | null;
}

export interface DoorScanAnswer {
  code: 'SCANNED' | 'ALREADY_SCANNED' | 'OTHER_PARCEL' | 'SCAN_PIECE_LABELS' | 'UNKNOWN';
  barcode: string;
  /** checked on the phone (weak signal); sent when the signal is back */
  offline?: boolean;
  piece?: string;
  scannedCount?: number;
  pieceCount?: number;
}

export interface PhotoAnswer {
  id: string;
  kind: PodKind | 'ATTEMPT';
  sizeBytes: number;
  takenAt: string;
}

export interface AttemptView {
  id: string;
  awb: string;
  hasPhone: boolean;
  hasAltPhone: boolean;
  rules: { callsRequired: number; minMinutesBetweenCalls: number; whatsappOnFailure: boolean; gpsPhotoOnFailure: boolean };
  reasons: { id: string; name: string; requiresCallProof: boolean; requiresPhoto: boolean }[];
  /** calls on this parcel; valid = calls far enough apart to count */
  calls: ParcelCalls & { valid: number };
  photo: { id: string; takenAt: string; hasGps: boolean } | null;
}

export interface FailAnswer {
  attemptId: string;
  status: 'FAILED';
  callsCounted: number;
  whatsappSent: boolean;
}

export interface DeliverAnswer {
  deliveryId: string;
  status: 'DELIVERED' | 'DELIVERED_LINK_PENDING';
  paymentMethod: PaymentMethod | null;
  codFils: number;
  changeFils: number | null;
}

/** Calls made from the app on this parcel today, and when the next one is allowed. */
export interface ParcelCalls {
  list: { at: string; kind: 'AWB' | 'ALT' }[];
  /** null: calling is allowed now */
  nextCallAt: string | null;
  lockMinutes: number;
}

export interface CallAnswer {
  /** tel: link for the phone's dialler */
  tel: string;
  calledAt: string;
  nextCallAt: string;
  /** parcels at the same stop with the same number count the call too */
  shipmentIds: string[];
}

export interface ShiftView {
  id: string;
  startedAt: string;
  endedAt: string | null;
  endedBy: 'COURIER' | 'AUTO' | null;
  startedWithGps: boolean;
}

export interface ShiftState {
  shift: ShiftView | null;
  lastShift: ShiftView | null;
}

/** The latest damaged-label request on a parcel (screen 7). */
export interface ManualAwbStatus {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  piece: string;
  context: 'LOAD' | 'DELIVERY';
  decisionNote: string | null;
  requestedAt: string;
}

export interface LoadView {
  parcels: {
    id: string;
    awb: string;
    pieceCount: number;
    loadedCount: number;
    pieces: { label: string; loaded: boolean; inHub: boolean }[];
    missingReported: boolean;
    manual: ManualAwbStatus | null;
  }[];
  pieces: { loaded: number; total: number; percent: number };
  /** parcels with every piece loaded */
  ready: number;
  routeStart: RouteStartCheck;
}

/** Route-start block (step 11): cash from earlier days not handed over, unless the hub manager allowed it. */
export interface RouteStartCheck {
  allowed: boolean;
  reason: string | null;
  earlierFils: number;
  allowedByHub?: boolean;
}

/** My cash (step 11). */
export interface MyCash {
  inHandFils: number;
  todayFils: number;
  earlierFils: number;
  collectedToday: { awb: string | null; amountFils: number; at: string }[];
  receipts: { id: string; number: string; receivedFils: number; expectedFils: number; at: string; receivedBy: string | null }[];
  routeStart: { allowed: boolean; earlierFils: number; exception: { reason: string; at: string } | null };
}

export interface CashQr {
  code: string;
  svg: string;
  expiresAt: string;
  inHandFils: number;
}

export interface LoadScanAnswer {
  code: 'LOADED' | 'ALREADY_LOADED' | 'ALREADY_OUT' | 'NOT_YOURS' | 'NOT_IN_HUB' | 'SCAN_PIECE_LABELS' | 'UNKNOWN';
  barcode: string;
  awb?: string;
  piece?: string;
  loadedCount?: number;
  pieceCount?: number;
  parcelLoaded?: boolean;
}

export interface RouteStartAnswer {
  out: number;
  leftBehind: { awb: string; loadedCount: number; pieceCount: number }[];
}
